import React, { useEffect, useState, useMemo } from "react";

const BASE_URL = "https://api.visiontrackcrm.com";

export default function SubscriptionApproval() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStore, setSelectedStore] = useState(null);

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BASE_URL}/registration/stores`);
      const result = await response.json();

      if (result.success && Array.isArray(result.data)) {
        setSubscriptions(result.data);
      } else {
        setSubscriptions([]);
      }
    } catch (error) {
      console.error("Fetch stores error:", error);
    } finally {
      setLoading(false);
    }
  };

  const approveSubscription = async (id, storeName) => {
    const confirmApprove = window.confirm(
      `Approve and activate store "${storeName || id}"? The store owner and staff will be granted full system access.`
    );
    if (!confirmApprove) return;

    try {
      setActionLoading(id);
      const res = await fetch(`${BASE_URL}/registration/approve-subscription/${id}`, {
        method: "PUT",
      });
      const data = await res.json();
      if (data?.success) {
        alert("✅ Store subscription approved and activated successfully!");
        if (selectedStore?.id === id) {
          setSelectedStore((prev) => prev ? { ...prev, subscription_status: "ACTIVE" } : null);
        }
        await fetchSubscriptions();
      } else {
        alert(data?.message || "Failed to approve subscription");
      }
    } catch (error) {
      console.error(error);
      alert("Error approving subscription");
    } finally {
      setActionLoading(null);
    }
  };

  const deactivateStore = async (id, storeName) => {
    const confirmDeact = window.confirm(
      `⚠️ Are you sure you want to DEACTIVATE "${storeName || id}"?\n\nNeither the store owner nor any staff/employee from this store will be able to log in while deactivated.`
    );
    if (!confirmDeact) return;

    try {
      setActionLoading(id);
      const res = await fetch(`${BASE_URL}/registration/deactivate-subscription/${id}`, {
        method: "PUT",
      });
      const data = await res.json();
      if (data?.success) {
        alert("🚫 Store has been deactivated. Login access disabled for all store accounts.");
        if (selectedStore?.id === id) {
          setSelectedStore((prev) => prev ? { ...prev, subscription_status: "DEACTIVATED" } : null);
        }
        await fetchSubscriptions();
      } else {
        alert(data?.message || "Failed to deactivate store");
      }
    } catch (error) {
      console.error(error);
      alert("Error deactivating store");
    } finally {
      setActionLoading(null);
    }
  };

  const activateStore = async (id, storeName) => {
    const confirmAct = window.confirm(
      `Re-activate "${storeName || id}"? Store owner and staff login access will be immediately restored.`
    );
    if (!confirmAct) return;

    try {
      setActionLoading(id);
      const res = await fetch(`${BASE_URL}/registration/activate-subscription/${id}`, {
        method: "PUT",
      });
      const data = await res.json();
      if (data?.success) {
        alert("✅ Store re-activated successfully! Access is restored.");
        if (selectedStore?.id === id) {
          setSelectedStore((prev) => prev ? { ...prev, subscription_status: "ACTIVE" } : null);
        }
        await fetchSubscriptions();
      } else {
        alert(data?.message || "Failed to activate store");
      }
    } catch (error) {
      console.error(error);
      alert("Error activating store");
    } finally {
      setActionLoading(null);
    }
  };

  const rejectSubscription = async (id, storeName) => {
    const confirmReject = window.confirm(
      `Reject subscription application for "${storeName || id}"?`
    );
    if (!confirmReject) return;

    try {
      setActionLoading(id);
      const res = await fetch(`${BASE_URL}/registration/reject-subscription/${id}`, {
        method: "PUT",
      });
      const data = await res.json();
      if (data?.success) {
        alert("❌ Subscription rejected.");
        if (selectedStore?.id === id) {
          setSelectedStore((prev) => prev ? { ...prev, subscription_status: "REJECTED" } : null);
        }
        await fetchSubscriptions();
      } else {
        alert(data?.message || "Failed to reject subscription");
      }
    } catch (error) {
      console.error(error);
      alert("Error rejecting subscription");
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (date, withTime = false) => {
    if (!date) return "-";
    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return String(date).split("T")[0];
      const options = {
        day: "2-digit",
        month: "short",
        year: "numeric",
      };
      if (withTime) {
        options.hour = "2-digit";
        options.minute = "2-digit";
      }
      return d.toLocaleDateString("en-IN", options);
    } catch (e) {
      return String(date);
    }
  };

  const getNormStatus = (store) => {
    return String(store.subscription_status || store.status || "PENDING").trim().toUpperCase();
  };

  // Filter & Search
  const filteredStores = useMemo(() => {
    return subscriptions.filter((s) => {
      const st = getNormStatus(s);
      if (filter !== "all") {
        if (filter === "PENDING" && st !== "PENDING") return false;
        if (filter === "ACTIVE" && st !== "ACTIVE") return false;
        if (filter === "DEACTIVATED" && (st !== "DEACTIVATED" && st !== "INACTIVE")) return false;
        if (filter === "REJECTED" && st !== "REJECTED") return false;
      }

      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const code = String(s.store_code || "").toLowerCase();
        const name = String(s.store_name || "").toLowerCase();
        const owner = String(s.owner_name || "").toLowerCase();
        const email = String(s.email || "").toLowerCase();
        const mobile = String(s.mobile || "").toLowerCase();
        const plan = String(s.plan_name || "").toLowerCase();
        return (
          code.includes(q) ||
          name.includes(q) ||
          owner.includes(q) ||
          email.includes(q) ||
          mobile.includes(q) ||
          plan.includes(q)
        );
      }
      return true;
    });
  }, [subscriptions, filter, searchQuery]);

  // Counts
  const counts = useMemo(() => {
    let pending = 0;
    let active = 0;
    let deactivated = 0;
    let rejected = 0;

    subscriptions.forEach((s) => {
      const st = getNormStatus(s);
      if (st === "PENDING") pending++;
      else if (st === "ACTIVE") active++;
      else if (st === "DEACTIVATED" || st === "INACTIVE") deactivated++;
      else if (st === "REJECTED") rejected++;
    });

    return { all: subscriptions.length, pending, active, deactivated, rejected };
  }, [subscriptions]);

  return (
    <div className="sub-approval-page">
      <style>{`
        .sub-approval-page {
          width: 100%;
          font-family: 'Inter', -apple-system, sans-serif;
          color: #0f172a;
        }

        .header-container {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 24px;
          flex-wrap: wrap;
          gap: 16px;
        }

        .page-title {
          font-size: 26px;
          font-weight: 800;
          color: #0f172a;
          margin: 0 0 6px 0;
          letter-spacing: -0.02em;
        }

        .page-desc {
          margin: 0;
          color: #64748b;
          font-size: 14px;
        }

        .refresh-btn {
          background: #0067b1;
          color: white;
          border: none;
          padding: 10px 18px;
          border-radius: 10px;
          font-weight: 600;
          font-size: 13.5px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          transition: all 0.2s ease;
          box-shadow: 0 4px 12px rgba(0, 103, 177, 0.2);
        }

        .refresh-btn:hover {
          background: #00528e;
          transform: translateY(-1px);
        }

        /* FILTERS & SEARCH ROW */
        .controls-card {
          background: white;
          border-radius: 14px;
          padding: 16px 20px;
          border: 1px solid #e2e8f0;
          margin-bottom: 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 16px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }

        .filter-tabs {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .tab-btn {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          color: #475569;
          padding: 8px 14px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          transition: all 0.15s ease;
        }

        .tab-btn:hover {
          background: #f1f5f9;
          color: #0f172a;
        }

        .tab-btn.active {
          background: #0067b1;
          border-color: #0067b1;
          color: white;
        }

        .tab-badge {
          background: #e2e8f0;
          color: #334155;
          padding: 2px 7px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 700;
        }

        .tab-btn.active .tab-badge {
          background: rgba(255, 255, 255, 0.25);
          color: white;
        }

        .tab-badge.pending-badge {
          background: #ef4444;
          color: white;
        }

        .search-box {
          position: relative;
          min-width: 280px;
          max-width: 380px;
          flex: 1;
        }

        .search-box input {
          width: 100%;
          padding: 9px 14px 9px 36px;
          border-radius: 8px;
          border: 1px solid #cbd5e1;
          font-size: 13.5px;
          outline: none;
          transition: border-color 0.2s;
        }

        .search-box input:focus {
          border-color: #0067b1;
          box-shadow: 0 0 0 3px rgba(0, 103, 177, 0.1);
        }

        .search-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
          font-size: 14px;
        }

        /* TABLE CARD */
        .table-card {
          background: white;
          border-radius: 14px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.02);
        }

        .table-responsive {
          width: 100%;
          overflow-x: auto;
        }

        table {
          width: 100%;
          border-collapse: collapse;
          text-align: left;
        }

        thead th {
          background: #f8fafc;
          color: #475569;
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          padding: 14px 18px;
          border-bottom: 1px solid #e2e8f0;
          white-space: nowrap;
        }

        tbody td {
          padding: 16px 18px;
          border-bottom: 1px solid #f1f5f9;
          color: #334155;
          font-size: 13.5px;
          vertical-align: middle;
        }

        tbody tr:hover {
          background-color: #f8fafc;
        }

        .store-code-pill {
          font-family: 'JetBrains Mono', monospace;
          background: #eff6ff;
          color: #0067b1;
          padding: 4px 8px;
          border-radius: 6px;
          font-weight: 700;
          font-size: 12px;
          border: 1px solid #bfdbfe;
          display: inline-block;
        }

        .store-name-strong {
          font-weight: 700;
          color: #0f172a;
          display: block;
        }

        .owner-sub {
          font-size: 12px;
          color: #64748b;
        }

        /* STATUS BADGES */
        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 5px 11px;
          border-radius: 20px;
          font-size: 11.5px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }

        .status-active {
          background: #dcfce7;
          color: #15803d;
          border: 1px solid #bbf7d0;
        }

        .status-pending {
          background: #fef3c7;
          color: #b45309;
          border: 1px solid #fde68a;
        }

        .status-deactivated, .status-inactive {
          background: #fee2e2;
          color: #b91c1c;
          border: 1px solid #fecaca;
        }

        .status-rejected {
          background: #fef2f2;
          color: #991b1b;
          border: 1px solid #fecaca;
        }

        .payment-badge {
          display: inline-block;
          padding: 4px 9px;
          border-radius: 12px;
          font-size: 11.5px;
          font-weight: 700;
        }

        .payment-paid {
          background: #dcfce7;
          color: #166534;
        }

        .payment-pending {
          background: #fef9c3;
          color: #854d0e;
        }

        /* ACTIONS */
        .actions-cell {
          white-space: nowrap;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .btn-act {
          border: none;
          padding: 7px 12px;
          border-radius: 7px;
          font-size: 12.5px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s ease;
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }

        .btn-act:hover {
          transform: translateY(-1px);
        }

        .btn-act:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        .btn-approve {
          background: #10b981;
          color: white;
          box-shadow: 0 2px 6px rgba(16, 185, 129, 0.25);
        }

        .btn-approve:hover {
          background: #059669;
        }

        .btn-deactivate {
          background: #ef4444;
          color: white;
          box-shadow: 0 2px 6px rgba(239, 68, 68, 0.25);
        }

        .btn-deactivate:hover {
          background: #dc2626;
        }

        .btn-activate {
          background: #0284c7;
          color: white;
          box-shadow: 0 2px 6px rgba(2, 132, 199, 0.25);
        }

        .btn-activate:hover {
          background: #0369a1;
        }

        .btn-reject {
          background: #f1f5f9;
          color: #dc2626;
          border: 1px solid #fecaca;
        }

        .btn-reject:hover {
          background: #fee2e2;
        }

        .btn-details {
          background: #eff6ff;
          color: #0067b1;
          border: 1px solid #bfdbfe;
        }

        .btn-details:hover {
          background: #dbeafe;
        }

        /* MODAL STYLES */
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(15, 23, 42, 0.6);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .modal-content {
          background: white;
          width: 100%;
          max-width: 620px;
          border-radius: 16px;
          box-shadow: 0 20px 40px rgba(0,0,0,0.2);
          overflow: hidden;
          animation: modalPop 0.2s cubic-bezier(0.16, 1, 0.3, 1);
        }

        @keyframes modalPop {
          from { opacity: 0; transform: scale(0.96); }
          to { opacity: 1; transform: scale(1); }
        }

        .modal-header {
          padding: 20px 24px;
          border-bottom: 1px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: #f8fafc;
        }

        .modal-header h2 {
          margin: 0;
          font-size: 19px;
          font-weight: 800;
          color: #0f172a;
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 24px;
          color: #94a3b8;
          cursor: pointer;
          padding: 0;
          line-height: 1;
        }

        .modal-close:hover {
          color: #0f172a;
        }

        .modal-body {
          padding: 24px;
          max-height: 75vh;
          overflow-y: auto;
        }

        .detail-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
          margin-bottom: 20px;
        }

        .detail-item {
          background: #f8fafc;
          padding: 12px 14px;
          border-radius: 10px;
          border: 1px solid #f1f5f9;
        }

        .detail-item.full-width {
          grid-column: span 2;
        }

        .detail-label {
          font-size: 11.5px;
          font-weight: 700;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 4px;
          display: block;
        }

        .detail-val {
          font-size: 14.5px;
          font-weight: 600;
          color: #0f172a;
          word-break: break-word;
        }

        .modal-footer {
          padding: 16px 24px;
          background: #f8fafc;
          border-top: 1px solid #e2e8f0;
          display: flex;
          justify-content: flex-end;
          gap: 10px;
        }
      `}</style>

      {/* HEADER */}
      <div className="header-container">
        <div>
          <h1 className="page-title">Store Subscription Approval</h1>
          <p className="page-desc">
            Manage store registration activations, verify subscription payments, and control access permissions.
          </p>
        </div>

        <button className="refresh-btn" onClick={fetchSubscriptions} disabled={loading}>
          ↻ Refresh Stores
        </button>
      </div>

      {/* FILTERS & SEARCH */}
      <div className="controls-card">
        <div className="filter-tabs">
          <button
            className={`tab-btn ${filter === "all" ? "active" : ""}`}
            onClick={() => setFilter("all")}
          >
            All Stores
            <span className="tab-badge">{counts.all}</span>
          </button>

          <button
            className={`tab-btn ${filter === "PENDING" ? "active" : ""}`}
            onClick={() => setFilter("PENDING")}
          >
            Pending Activation
            <span className={`tab-badge ${counts.pending > 0 ? "pending-badge" : ""}`}>
              {counts.pending}
            </span>
          </button>

          <button
            className={`tab-btn ${filter === "ACTIVE" ? "active" : ""}`}
            onClick={() => setFilter("ACTIVE")}
          >
            Active
            <span className="tab-badge">{counts.active}</span>
          </button>

          <button
            className={`tab-btn ${filter === "DEACTIVATED" ? "active" : ""}`}
            onClick={() => setFilter("DEACTIVATED")}
          >
            Deactivated
            <span className="tab-badge">{counts.deactivated}</span>
          </button>

          <button
            className={`tab-btn ${filter === "REJECTED" ? "active" : ""}`}
            onClick={() => setFilter("REJECTED")}
          >
            Rejected
            <span className="tab-badge">{counts.rejected}</span>
          </button>
        </div>

        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search by code, store, owner, phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* TABLE */}
      <div className="table-card">
        <div className="table-responsive">
          <table>
            <thead>
              <tr>
                <th>Store Code</th>
                <th>Store Name</th>
                <th>Owner</th>
                <th>Plan</th>
                <th>Amount</th>
                <th>Payment</th>
                <th>Member Since</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
                    Loading stores catalog...
                  </td>
                </tr>
              ) : filteredStores.length === 0 ? (
                <tr>
                  <td colSpan="9" style={{ textAlign: "center", padding: "40px", color: "#64748b" }}>
                    No stores found matching current filter or search criteria.
                  </td>
                </tr>
              ) : (
                filteredStores.map((store) => {
                  const status = getNormStatus(store);
                  const isPending = status === "PENDING";
                  const isActive = status === "ACTIVE";
                  const isDeactivated = status === "DEACTIVATED" || status === "INACTIVE";
                  const isRejected = status === "REJECTED";

                  return (
                    <tr key={store.id}>
                      <td>
                        <span className="store-code-pill">{store.store_code || `STORE${store.id}`}</span>
                      </td>

                      <td>
                        <strong className="store-name-strong">{store.store_name}</strong>
                        {store.email && <span className="owner-sub">{store.email}</span>}
                      </td>

                      <td>
                        <div>{store.owner_name}</div>
                        {store.mobile && <span className="owner-sub">📞 {store.mobile}</span>}
                      </td>

                      <td>
                        <strong>{store.plan_name || "Standard Plan"}</strong>
                      </td>

                      <td>
                        <strong>₹{store.amount || "0.00"}</strong>
                      </td>

                      <td>
                        <span
                          className={`payment-badge ${
                            store.razorpay_payment_id ? "payment-paid" : "payment-pending"
                          }`}
                        >
                          {store.razorpay_payment_id ? "PAID" : "PENDING"}
                        </span>
                      </td>

                      <td>{formatDate(store.created_at)}</td>

                      <td>
                        <span className={`status-badge status-${status.toLowerCase()}`}>
                          {isPending && "⏳ "}
                          {isActive && "✅ "}
                          {isDeactivated && "🚫 "}
                          {isRejected && "❌ "}
                          {status}
                        </span>
                      </td>

                      {/* DYNAMIC ACTIONS */}
                      <td>
                        <div className="actions-cell">
                          {/* FOR PENDING: APPROVE & REJECT */}
                          {isPending && (
                            <>
                              <button
                                className="btn-act btn-approve"
                                disabled={actionLoading === store.id}
                                onClick={() => approveSubscription(store.id, store.store_name)}
                              >
                                {actionLoading === store.id ? "..." : "Approve"}
                              </button>

                              <button
                                className="btn-act btn-reject"
                                disabled={actionLoading === store.id}
                                onClick={() => rejectSubscription(store.id, store.store_name)}
                              >
                                {actionLoading === store.id ? "..." : "Reject"}
                              </button>
                            </>
                          )}

                          {/* FOR ACTIVE: DEACTIVATE */}
                          {isActive && (
                            <button
                              className="btn-act btn-deactivate"
                              disabled={actionLoading === store.id}
                              onClick={() => deactivateStore(store.id, store.store_name)}
                            >
                              {actionLoading === store.id ? "..." : "Deactivate"}
                            </button>
                          )}

                          {/* FOR DEACTIVATED: ACTIVATE */}
                          {isDeactivated && (
                            <button
                              className="btn-act btn-activate"
                              disabled={actionLoading === store.id}
                              onClick={() => activateStore(store.id, store.store_name)}
                            >
                              {actionLoading === store.id ? "..." : "Activate"}
                            </button>
                          )}

                          {/* FOR REJECTED: APPROVE / ACTIVATE */}
                          {isRejected && (
                            <button
                              className="btn-act btn-activate"
                              disabled={actionLoading === store.id}
                              onClick={() => activateStore(store.id, store.store_name)}
                            >
                              {actionLoading === store.id ? "..." : "Activate"}
                            </button>
                          )}

                          {/* DETAILS BUTTON FOR ALL */}
                          <button
                            className="btn-act btn-details"
                            onClick={() => setSelectedStore(store)}
                          >
                            Details
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* POPUP / MODAL: STORE DETAILS */}
      {selectedStore && (
        <div className="modal-overlay" onClick={() => setSelectedStore(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>{selectedStore.store_name}</h2>
                <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
                  Code: <strong style={{ color: "#0067b1" }}>{selectedStore.store_code}</strong>
                </div>
              </div>
              <button className="modal-close" onClick={() => setSelectedStore(null)}>
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label">Store Owner</span>
                  <div className="detail-val">{selectedStore.owner_name || "-"}</div>
                </div>

                <div className="detail-item">
                  <span className="detail-label">Status</span>
                  <div className="detail-val">
                    <span
                      className={`status-badge status-${getNormStatus(selectedStore).toLowerCase()}`}
                    >
                      {getNormStatus(selectedStore)}
                    </span>
                  </div>
                </div>

                <div className="detail-item">
                  <span className="detail-label">Owner Email</span>
                  <div className="detail-val">{selectedStore.email || "-"}</div>
                </div>

                <div className="detail-item">
                  <span className="detail-label">Owner Mobile</span>
                  <div className="detail-val">📞 {selectedStore.mobile || "-"}</div>
                </div>

                <div className="detail-item full-width">
                  <span className="detail-label">Store Address</span>
                  <div className="detail-val">{selectedStore.address || "No address provided."}</div>
                </div>

                <div className="detail-item">
                  <span className="detail-label">Subscription Plan</span>
                  <div className="detail-val">{selectedStore.plan_name || "Starter Plan"}</div>
                </div>

                <div className="detail-item">
                  <span className="detail-label">Subscription Amount</span>
                  <div className="detail-val">₹{selectedStore.amount || "0.00"}</div>
                </div>

                <div className="detail-item">
                  <span className="detail-label">Payment Status</span>
                  <div className="detail-val">
                    <span
                      className={`payment-badge ${
                        selectedStore.razorpay_payment_id ? "payment-paid" : "payment-pending"
                      }`}
                    >
                      {selectedStore.razorpay_payment_id ? "PAID" : "PENDING"}
                    </span>
                    {selectedStore.razorpay_payment_id && (
                      <div style={{ fontSize: "11px", color: "#64748b", marginTop: "4px" }}>
                        Txn: {selectedStore.razorpay_payment_id}
                      </div>
                    )}
                  </div>
                </div>

                <div className="detail-item">
                  <span className="detail-label">Member Since (Joining Date)</span>
                  <div className="detail-val">
                    📅 {formatDate(selectedStore.created_at, true)}
                  </div>
                </div>

                {selectedStore.expiry_date && (
                  <div className="detail-item full-width">
                    <span className="detail-label">Plan Expiry Date</span>
                    <div className="detail-val">
                      ⏳ {formatDate(selectedStore.expiry_date, true)}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              {getNormStatus(selectedStore) === "PENDING" && (
                <>
                  <button
                    className="btn-act btn-reject"
                    disabled={actionLoading === selectedStore.id}
                    onClick={() => rejectSubscription(selectedStore.id, selectedStore.store_name)}
                  >
                    Reject Application
                  </button>
                  <button
                    className="btn-act btn-approve"
                    disabled={actionLoading === selectedStore.id}
                    onClick={() => approveSubscription(selectedStore.id, selectedStore.store_name)}
                  >
                    Approve & Activate
                  </button>
                </>
              )}

              {getNormStatus(selectedStore) === "ACTIVE" && (
                <button
                  className="btn-act btn-deactivate"
                  disabled={actionLoading === selectedStore.id}
                  onClick={() => deactivateStore(selectedStore.id, selectedStore.store_name)}
                >
                  Deactivate Store
                </button>
              )}

              {(getNormStatus(selectedStore) === "DEACTIVATED" ||
                getNormStatus(selectedStore) === "INACTIVE" ||
                getNormStatus(selectedStore) === "REJECTED") && (
                <button
                  className="btn-act btn-activate"
                  disabled={actionLoading === selectedStore.id}
                  onClick={() => activateStore(selectedStore.id, selectedStore.store_name)}
                >
                  Re-Activate Store
                </button>
              )}

              <button
                className="btn-act btn-details"
                onClick={() => setSelectedStore(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}